import { useCallback, useEffect, useRef } from 'react';

import { bridgeAtom, isOpenChatAtom, useAtom, useValue, widgetPopupsStatesAtom } from '@/store';

import { PopupState, tooltipConfig, WidgetPopupType } from '@/modules/models';

import { usePausableTimeout } from './usePausableTimeout';

const DEFAULT_DELAY = 15000;
const DEFAULT_DURATION = 8000;

interface UseStuckPopupProps {
  isDisabled: boolean;
  activePopup: WidgetPopupType | null;
  isHover: boolean;
  setPopupState: (popupType: WidgetPopupType, state: PopupState) => void;
  showPopup: (popup: WidgetPopupType) => void;
  hidePopup: () => void;
}
export const useStuckPopup = ({ activePopup, isHover, showPopup, hidePopup, setPopupState }: UseStuckPopupProps) => {
  const isOpenChat = useValue(isOpenChatAtom);
  const { widget } = useValue(bridgeAtom);
  const [widgetPopupsStates, setWidgetPopupsStates] = useAtom(widgetPopupsStatesAtom);

  const { INIT_WITH_CLOSE_BTN, STUCK_ON_PAGE } = widgetPopupsStates;

  const { stuckTooltip } = widget || {};
  const delay = (stuckTooltip as tooltipConfig)?.delay || DEFAULT_DELAY;
  const duration = (stuckTooltip as tooltipConfig)?.duration || DEFAULT_DURATION;
  const pauseOnHover = (stuckTooltip as tooltipConfig)?.pauseOnHover;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { start } = usePausableTimeout(isHover, pauseOnHover, hidePopup);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      setWidgetPopupsStates((prevState) => ({
        ...prevState,
        [WidgetPopupType.STUCK_ON_PAGE]: PopupState.IDLE,
      }));
    }
  }, [setWidgetPopupsStates]);

  const startStuckTimer = useCallback(() => {
    setWidgetPopupsStates((prevState) => ({
      ...prevState,
      [WidgetPopupType.STUCK_ON_PAGE]: PopupState.STARTED,
    }));
    timerRef.current = setTimeout(() => {
      showPopup(WidgetPopupType.STUCK_ON_PAGE);
    }, delay);
  }, [delay, showPopup, setWidgetPopupsStates]);

  useEffect(() => {
    if (
      isOpenChat ||
      !stuckTooltip ||
      activePopup !== null ||
      timerRef.current ||
      STUCK_ON_PAGE === PopupState.SEEN ||
      INIT_WITH_CLOSE_BTN !== PopupState.CLOSED
    ) {
      return;
    }

    startStuckTimer();
  }, [isOpenChat, activePopup, stuckTooltip, delay, INIT_WITH_CLOSE_BTN, STUCK_ON_PAGE, startStuckTimer]);

  useEffect(() => {
    if ((activePopup !== null && activePopup !== WidgetPopupType.STUCK_ON_PAGE) || isOpenChat) {
      clearTimer();
    }
  }, [isOpenChat, activePopup, clearTimer]);

  useEffect(() => {
    if (STUCK_ON_PAGE === PopupState.VISIBLE) {
      start(duration);
      setPopupState(WidgetPopupType.STUCK_ON_PAGE, PopupState.SEEN);
    }
  }, [STUCK_ON_PAGE, start, duration, setPopupState]);

  return null;
};
